import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Clock, Send, ArrowRight, MessageSquare } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';
import axios from 'axios';
import SEO from '../components/SEO';
import { 
  PageHeader, 
  Section, 
  Container, 
  SectionHeader,
  FadeInOnScroll,
  PrimaryButton
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const { phone, email, address, contactSettings, companyInfo, loading: siteLoading } = useSiteData();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiryType: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get enquiry types from settings
  const enquiryTypes = contactSettings?.enquiryTypes?.filter(t => t.isActive !== false) || [
    { id: 'general', label: 'General Enquiry' },
    { id: 'projects', label: 'Project Enquiry' },
    { id: 'careers', label: 'Careers' }
  ];

  // Page content from settings
  const pageTitle = contactSettings?.pageTitle || 'Contact Us';
  const pageSubtitle = contactSettings?.pageSubtitle || 'Get in touch with our team to discuss your MEP engineering needs.';
  const introText = contactSettings?.introText || "Whether you're planning a new build, refurbishment, or need expert consultancy services, our team is ready to assist you with professional MEP engineering solutions.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/xnjbqppr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `FURD Contact Form: ${formData.enquiryType}`,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          enquiryType: formData.enquiryType,
          message: formData.message,
          submittedAt: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        // Also save to database for records
        try {
          await axios.post(`${API}/contact`, {
            ...formData,
            submittedAt: new Date().toISOString()
          });
        } catch (dbError) {
          console.log('Database save optional:', dbError);
        }
        
        toast({
          title: "Message Sent!",
          description: contactSettings?.autoReplyMessage || "Thank you for contacting us. We'll get back to you shortly.",
        });
        setFormData({ name: '', email: '', phone: '', enquiryType: 'general', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <SEO 
        title={pageTitle}
        description={pageSubtitle}
      />
      
      {/* Unified Hero + MEP Engineering Showcase */}
      <section className="pt-24 pb-20 md:pt-28 md:pb-32 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Animated particle background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Contact Us Title - Integrated Hero */}
          <FadeInOnScroll>
            <div className="text-center mb-10">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight">
                {pageTitle}
              </h1>
            </div>
          </FadeInOnScroll>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-cyan-500/50" />
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-400 font-semibold text-xs uppercase tracking-widest border border-cyan-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
              Engineering Excellence
            </span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-cyan-500/50" />
          </div>

          <FadeInOnScroll delay={100}>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
                Bringing Buildings to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Life</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-3xl mx-auto">
                Every system, every connection, every detail engineered for perfection
              </p>
            </div>
          </FadeInOnScroll>

          {/* Animated Building Visualization */}
          <FadeInOnScroll delay={200}>
            <div className="relative max-w-4xl mx-auto mb-16">
              {/* Building SVG with animated systems */}
              <svg viewBox="0 0 800 500" className="w-full h-auto">
                {/* Grid lines background */}
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(6,182,212,0.1)" strokeWidth="0.5"/>
                  </pattern>
                  
                  {/* Glow filters */}
                  <filter id="glow-cyan" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  <filter id="glow-blue" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  
                  {/* Animated gradient for pipes */}
                  <linearGradient id="flow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0">
                      <animate attributeName="offset" values="-1;1" dur="2s" repeatCount="indefinite"/>
                    </stop>
                    <stop offset="50%" stopColor="#06b6d4" stopOpacity="1">
                      <animate attributeName="offset" values="-0.5;1.5" dur="2s" repeatCount="indefinite"/>
                    </stop>
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0">
                      <animate attributeName="offset" values="0;2" dur="2s" repeatCount="indefinite"/>
                    </stop>
                  </linearGradient>
                </defs>
                
                <rect width="800" height="500" fill="url(#grid)"/>
                
                {/* Building structure */}
                <g className="building-structure">
                  {/* Main building outline */}
                  <rect x="200" y="80" width="400" height="350" fill="none" stroke="rgba(148,163,184,0.3)" strokeWidth="2" rx="4"/>
                  
                  {/* Floors */}
                  {[0,1,2,3,4].map(floor => (
                    <g key={floor}>
                      <line x1="200" y1={150 + floor * 70} x2="600" y2={150 + floor * 70} stroke="rgba(148,163,184,0.2)" strokeWidth="1"/>
                      {/* Windows */}
                      {[0,1,2,3,4].map(win => (
                        <rect 
                          key={win} 
                          x={230 + win * 72} 
                          y={95 + floor * 70} 
                          width="40" 
                          height="45" 
                          fill="rgba(6,182,212,0.1)" 
                          stroke="rgba(6,182,212,0.3)" 
                          strokeWidth="1"
                          rx="2"
                        >
                          <animate 
                            attributeName="fill" 
                            values="rgba(6,182,212,0.1);rgba(6,182,212,0.3);rgba(6,182,212,0.1)" 
                            dur={`${2 + Math.random() * 2}s`} 
                            repeatCount="indefinite"
                            begin={`${Math.random() * 2}s`}
                          />
                        </rect>
                      ))}
                    </g>
                  ))}
                </g>
                
                {/* HVAC System - Top with flowing air */}
                <g className="hvac-system" filter="url(#glow-cyan)">
                  {/* Main duct */}
                  <path d="M 250 60 L 550 60" stroke="#06b6d4" strokeWidth="8" fill="none" opacity="0.6"/>
                  <path d="M 250 60 L 550 60" stroke="url(#flow-gradient)" strokeWidth="8" fill="none"/>
                  
                  {/* Vertical ducts */}
                  {[280, 400, 520].map((x, i) => (
                    <g key={i}>
                      <line x1={x} y1="60" x2={x} y2="420" stroke="#06b6d4" strokeWidth="4" opacity="0.4"/>
                      {/* Animated air particles */}
                      <circle r="3" fill="#06b6d4">
                        <animateMotion dur={`${3 + i}s`} repeatCount="indefinite">
                          <mpath href={`#duct-path-${i}`}/>
                        </animateMotion>
                      </circle>
                      <path id={`duct-path-${i}`} d={`M ${x} 60 L ${x} 420`} fill="none"/>
                    </g>
                  ))}
                  
                  {/* HVAC unit */}
                  <rect x="370" y="40" width="60" height="30" fill="#0e7490" stroke="#06b6d4" strokeWidth="2" rx="4"/>
                  <circle cx="385" cy="55" r="6" fill="none" stroke="#06b6d4" strokeWidth="2">
                    <animateTransform attributeName="transform" type="rotate" from="0 385 55" to="360 385 55" dur="1s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="415" cy="55" r="6" fill="none" stroke="#06b6d4" strokeWidth="2">
                    <animateTransform attributeName="transform" type="rotate" from="0 415 55" to="360 415 55" dur="1s" repeatCount="indefinite"/>
                  </circle>
                </g>
                
                {/* Electrical System - Yellow/Orange */}
                <g className="electrical-system">
                  {/* Main electrical riser */}
                  <line x1="180" y1="100" x2="180" y2="430" stroke="#f59e0b" strokeWidth="3" opacity="0.6"/>
                  
                  {/* Floor connections with pulse */}
                  {[0,1,2,3,4].map(floor => (
                    <g key={floor}>
                      <line 
                        x1="180" 
                        y1={130 + floor * 70} 
                        x2="200" 
                        y2={130 + floor * 70} 
                        stroke="#f59e0b" 
                        strokeWidth="2"
                        opacity="0.8"
                      />
                      <circle cx="180" cy={130 + floor * 70} r="4" fill="#f59e0b">
                        <animate 
                          attributeName="opacity" 
                          values="0.4;1;0.4" 
                          dur="1.5s" 
                          repeatCount="indefinite"
                          begin={`${floor * 0.3}s`}
                        />
                      </circle>
                    </g>
                  ))}
                  
                  {/* Electrical panel */}
                  <rect x="165" y="435" width="30" height="45" fill="#78350f" stroke="#f59e0b" strokeWidth="2" rx="2"/>
                  <line x1="172" y1="445" x2="188" y2="445" stroke="#f59e0b" strokeWidth="2"/>
                  <line x1="172" y1="455" x2="188" y2="455" stroke="#f59e0b" strokeWidth="2"/>
                  <line x1="172" y1="465" x2="188" y2="465" stroke="#f59e0b" strokeWidth="2"/>
                </g>
                
                {/* Plumbing System - Blue */}
                <g className="plumbing-system" filter="url(#glow-blue)">
                  {/* Main water riser */}
                  <line x1="620" y1="100" x2="620" y2="430" stroke="#3b82f6" strokeWidth="4" opacity="0.5"/>
                  
                  {/* Floor connections */}
                  {[0,1,2,3,4].map(floor => (
                    <g key={floor}>
                      <line 
                        x1="600" 
                        y1={130 + floor * 70} 
                        x2="620" 
                        y2={130 + floor * 70} 
                        stroke="#3b82f6" 
                        strokeWidth="3"
                        opacity="0.7"
                      />
                      {/* Animated water drops */}
                      <circle r="3" fill="#3b82f6">
                        <animate 
                          attributeName="cy" 
                          values={`${100 + floor * 70};${170 + floor * 70}`}
                          dur="2s" 
                          repeatCount="indefinite"
                          begin={`${floor * 0.4}s`}
                        />
                        <animate 
                          attributeName="cx" 
                          values="620;620"
                          dur="2s" 
                          repeatCount="indefinite"
                        />
                        <animate 
                          attributeName="opacity" 
                          values="1;0"
                          dur="2s" 
                          repeatCount="indefinite"
                          begin={`${floor * 0.4}s`}
                        />
                      </circle>
                    </g>
                  ))}
                  
                  {/* Water tank */}
                  <rect x="605" y="435" width="30" height="40" fill="#1e3a8a" stroke="#3b82f6" strokeWidth="2" rx="2"/>
                  <rect x="610" y="445" width="20" height="20" fill="#3b82f6" opacity="0.3">
                    <animate attributeName="height" values="20;10;20" dur="2s" repeatCount="indefinite"/>
                    <animate attributeName="y" values="445;455;445" dur="2s" repeatCount="indefinite"/>
                  </rect>
                </g>
                
                {/* Labels */}
                <g className="labels" fill="#94a3b8" fontSize="12" fontFamily="system-ui">
                  <text x="100" y="280" textAnchor="middle" transform="rotate(-90, 100, 280)">ELECTRICAL</text>
                  <text x="700" y="280" textAnchor="middle" transform="rotate(90, 700, 280)">PLUMBING</text>
                  <text x="400" y="25" textAnchor="middle">HVAC SYSTEMS</text>
                </g>
                
                {/* Connecting particles to CTA */}
                {[...Array(8)].map((_, i) => (
                  <circle key={i} r="2" fill="#06b6d4" opacity="0.6">
                    <animate 
                      attributeName="cx" 
                      values={`${300 + i * 30};400`}
                      dur={`${2 + i * 0.2}s`} 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="cy" 
                      values="430;500"
                      dur={`${2 + i * 0.2}s`} 
                      repeatCount="indefinite"
                    />
                    <animate 
                      attributeName="opacity" 
                      values="0.8;0"
                      dur={`${2 + i * 0.2}s`} 
                      repeatCount="indefinite"
                    />
                  </circle>
                ))}
              </svg>
              
              {/* System Labels */}
              <div className="flex justify-center gap-8 mt-8 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-cyan-400 text-sm font-medium">HVAC</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-amber-400 text-sm font-medium">Electrical</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-400 animate-pulse" />
                  <span className="text-blue-400 text-sm font-medium">Plumbing</span>
                </div>
              </div>
            </div>
          </FadeInOnScroll>

          {/* Particle CTA Section */}
          <FadeInOnScroll delay={400}>
            <div className="text-center relative">
              {/* Particle streams converging to button */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1.5 h-1.5 bg-cyan-400 rounded-full opacity-60"
                    style={{
                      animation: `particleConverge ${3 + Math.random() * 2}s infinite`,
                      animationDelay: `${Math.random() * 2}s`,
                      left: `${20 + Math.random() * 60}%`,
                      top: `${Math.random() * 100}%`
                    }}
                  />
                ))}
              </div>
              
              <a 
                href="/services"
                className="group relative inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white font-bold text-xl shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                {/* Animated border glow */}
                <span className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                
                {/* Pulse rings */}
                <span className="absolute inset-0 rounded-2xl border-2 border-cyan-400/50 animate-ping opacity-20" />
                <span className="absolute inset-2 rounded-xl border border-cyan-400/30 animate-pulse" />
                
                {/* Button content */}
                <span className="relative z-10 flex items-center gap-3">
                  <span>Explore Our Services</span>
                  <svg className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </a>
              
              <p className="mt-8 text-slate-400 text-lg">
                Ready to bring your project to life?
              </p>
            </div>
          </FadeInOnScroll>
        </div>
        
        {/* CSS for particle animation */}
        <style>{`
          @keyframes particleConverge {
            0% {
              transform: translate(0, 0) scale(1);
              opacity: 0;
            }
            50% {
              opacity: 0.8;
            }
            100% {
              transform: translate(calc(50vw - 100%), 50px) scale(0);
              opacity: 0;
            }
          }
        `}</style>
      </section>

      {/* Contact Information & Form */}
      <Section background="light" padding="default">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Information */}
            <FadeInOnScroll direction="left">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/10 to-blue-500/10 rounded-full text-blue-600 font-semibold text-sm uppercase tracking-widest mb-6 border border-blue-200/50">
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                  Contact Details
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                  Let&apos;s Start a <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">Conversation</span>
                </h2>
                <p className="text-lg text-gray-500 mb-10 leading-relaxed">
                  {introText}
                </p>

                <div className="space-y-4">
                  {/* Phone */}
                  <FadeInOnScroll delay={100}>
                    <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300 shadow-sm">
                          <Phone className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg mb-1">Phone</p>
                          <p className="text-gray-500">{phone || '+44 (0) 123 456 7890'}</p>
                        </div>
                      </div>
                    </div>
                  </FadeInOnScroll>

                  {/* Email */}
                  <FadeInOnScroll delay={200}>
                    <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300 shadow-sm">
                          <Mail className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg mb-1">Email</p>
                          <p className="text-gray-500">{email || 'info@furd.co.uk'}</p>
                        </div>
                      </div>
                    </div>
                  </FadeInOnScroll>

                  {/* Address */}
                  <FadeInOnScroll delay={300}>
                    <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300 shadow-sm">
                          <MapPin className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg mb-1">Address</p>
                          <p className="text-gray-500 whitespace-pre-line">{address || 'London, United Kingdom'}</p>
                        </div>
                      </div>
                    </div>
                  </FadeInOnScroll>

                  {/* Business Hours */}
                  <FadeInOnScroll delay={400}>
                    <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:from-blue-600 group-hover:to-blue-500 transition-all duration-300 shadow-sm">
                          <Clock className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-lg mb-1">Business Hours</p>
                          <p className="text-gray-500">Monday - Friday: 9:00 AM - 5:30 PM</p>
                        </div>
                      </div>
                    </div>
                  </FadeInOnScroll>
                </div>
              </div>
            </FadeInOnScroll>

            {/* Contact Form */}
            <FadeInOnScroll delay={200} direction="right">
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Send us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 (0) 20 1234 5678"
                      className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  {enquiryTypes.length > 1 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Enquiry Type</label>
                      <select
                        name="enquiryType"
                        value={formData.enquiryType}
                        onChange={handleChange}
                        className="w-full h-14 border border-gray-200 rounded-xl px-4 text-lg bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                        data-testid="contact-enquiry-type"
                      >
                        {enquiryTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Your Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or enquiry..."
                      rows={5}
                      required
                      className="rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold h-14 text-lg rounded-xl shadow-lg hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </FadeInOnScroll>
          </div>
        </Container>
      </Section>

      {/* Map Section - Optional */}
      {companyInfo?.mapEmbedUrl && (
        <section className="h-96 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent h-8 z-10" />
          <iframe
            src={companyInfo.mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Office Location"
          />
        </section>
      )}
    </div>
  );
};

export default Contact;
